import { estimateMutationScopeId } from '@/api/mutations/estimates/hydrateEstimateCaches';
import useAddEstimateAppliance from '@/api/mutations/estimates/useAddEstimateAppliance';
import useSearchAppliances from '@/api/queries/appliances/useSearchAppliances';
import useGetEstimateApplianceRecommendations from '@/api/queries/estimates/useGetEstimateApplianceRecommendations';
import useGetEstimateSolarApplianceRecommendations from '@/api/queries/estimates/useGetEstimateSolarApplianceRecommendations';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { ProductSpecCategory } from '@/gql/graphql';
import { usePermissions } from '@/hooks/usePermissions';
import {
  Alert,
  AnchorButton,
  Badge,
  Button,
  EmptyState,
  RawInput,
  Tooltip,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { useDebounce } from '@payaca/hooks';
import { AppliancesPermissions } from '@payaca/permissions/appliances/appliances.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { estimateSettingsPathForCategory } from '@/ui/components/companySettings/estimateSettingsRoutes';
import { ApplianceThumb, specChips } from './applianceDisplay';

type AppliancePickerProps = {
  projectId: string;
  estimateId: string;
  initialCategory: ProductSpecCategory;
  /** The Estimate section to add the picked Appliance's Items into. */
  sectionId: string;
  onClose: () => void;
};

type SpecForRow = {
  name: string;
  category: ProductSpecCategory;
  image?: { url: string } | null;
  nominalOutputKw?: number | null;
  soundPowerDbA?: number | null;
  type?: string | null;
  domesticHotWaterLitres?: number | null;
};

type ApplianceForRow = {
  id: string;
  items: { id: string }[];
  totalPrice: { value: number; currency: { code: string; exponent: number } };
  productSpec: SpecForRow;
};

const ApplianceRow: FC<{
  appliance: ApplianceForRow;
  recommended: boolean;
  alreadyLinked: boolean;
  disabled: boolean;
  onAdd: () => void;
}> = ({ appliance, recommended, alreadyLinked, disabled, onAdd }) => {
  const chips = specChips(appliance.productSpec);
  return (
    <div
      data-testid="appliance-picker-row"
      className="flex items-start gap-3 py-3"
    >
      <ApplianceThumb imageUrl={appliance.productSpec.image?.url} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium">{appliance.productSpec.name}</span>
          {recommended && (
            <Badge variant="soft" colour="green" size="sm" rounded>
              <UntitledIcon name="check" className="h-3 w-3" />
              Recommended
            </Badge>
          )}
        </div>
        {chips && <p className="text-sm text-gray-500">{chips}</p>}
        <p className="flex items-center gap-1 text-sm text-gray-500">
          <UntitledIcon name="shopping-cart-02.3" className="h-3.5 w-3.5" />
          Includes {appliance.items.length} item
          {appliance.items.length === 1 ? '' : 's'}
        </p>
      </div>
      <div className="flex flex-shrink-0 flex-col items-end gap-2">
        <span className="font-semibold">
          <CurrencyValue
            value={appliance.totalPrice.value}
            currency={appliance.totalPrice.currency}
          />
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={onAdd}
        >
          <UntitledIcon
            name={alreadyLinked ? 'check' : 'plus.3'}
            className="h-3.5 w-3.5"
          />
          {alreadyLinked ? 'Added' : 'Add'}
        </Button>
      </div>
    </div>
  );
};

/**
 * Display labels per pickable category. `plural` exists because not every
 * category pluralises with a bare "s" (batteries).
 */
const typeLabels: Partial<
  Record<ProductSpecCategory, { singular: string; plural: string }>
> = {
  HEAT_PUMP: { singular: 'heat pump', plural: 'heat pumps' },
  CYLINDER: { singular: 'water cylinder', plural: 'water cylinders' },
  SOLAR_PANEL: { singular: 'solar panel', plural: 'solar panels' },
  INVERTER: { singular: 'inverter', plural: 'inverters' },
  BATTERY: { singular: 'battery', plural: 'batteries' },
};

const SOLAR_CATEGORIES: ProductSpecCategory[] = [
  'SOLAR_PANEL',
  'INVERTER',
  'BATTERY',
];

const AppliancePicker: FC<AppliancePickerProps> = (props) => {
  const { projectId, estimateId, initialCategory, sectionId, onClose } = props;

  const category = initialCategory;
  const isSolarCategory = SOLAR_CATEGORIES.includes(category);
  const typeLabel = typeLabels[category] ??
    typeLabels.HEAT_PUMP ?? {
      singular: 'appliance',
      plural: 'appliances',
    };

  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const linkedApplianceIds = new Set(
    (estimate?.appliances ?? []).map((a) => a.id)
  );

  // Heat pump categories recommend from the heat loss snapshot; solar
  // categories from the solar potential snapshot. Only the relevant query is
  // enabled so HEAT_PUMP estimates behave exactly as before.
  const { recommendedHeatPumps, recommendedCylinders } =
    useGetEstimateApplianceRecommendations(
      isSolarCategory ? undefined : estimateId
    );
  const { recommendedPanels, recommendedInverters, recommendedBatteries } =
    useGetEstimateSolarApplianceRecommendations(
      isSolarCategory ? estimateId : undefined
    );
  let recommendations;
  switch (category) {
    case 'CYLINDER':
      recommendations = recommendedCylinders;
      break;
    case 'SOLAR_PANEL':
      recommendations = recommendedPanels;
      break;
    case 'INVERTER':
      recommendations = recommendedInverters;
      break;
    case 'BATTERY':
      recommendations = recommendedBatteries;
      break;
    default:
      recommendations = recommendedHeatPumps;
  }
  recommendations = recommendations ?? [];
  const recommendedApplianceIds = new Set(
    recommendations.map((r) => r.appliance.id)
  );

  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useDebounce(() => setDebouncedQuery(searchQuery), 300, [searchQuery]);

  const { data: searchResults, isLoading: isSearching } = useSearchAppliances(
    category,
    debouncedQuery || undefined
  );

  const addEstimateAppliance = useAddEstimateAppliance({
    projectId,
    estimateId,
    options: { scope: { id: estimateMutationScopeId(estimateId) } },
  });

  const isPending = addEstimateAppliance.isPending;

  // Use mutate (not mutateAsync) with an onSuccess that closes the picker, so a
  // failed add surfaces via the error Alert and keeps the picker open rather
  // than rejecting an unhandled promise from the click handler.
  const onSelect = (applianceId: string) => {
    addEstimateAppliance.mutate(
      { estimateId, applianceId, sectionId },
      { onSuccess: () => onClose() }
    );
  };

  const { userHasRequiredPermission } = usePermissions();
  // Only offer the settings shortcut to users who can actually add/edit
  // Appliances — a read-only viewer has nothing to configure there.
  const canManageEstimateSettings = userHasRequiredPermission([
    AppliancesPermissions.MANAGE_APPLIANCES,
  ]);
  const productSettingsLink = canManageEstimateSettings ? (
    <AnchorButton
      as={Link}
      to={estimateSettingsPathForCategory(initialCategory)}
      variant="link"
      size="sm"
    >
      Appliances settings
    </AnchorButton>
  ) : null;

  // The search returns the account's own Appliances for this category.
  const catalogueRows = (searchResults ?? []).map((appliance) => ({
    appliance,
  }));

  let catalogueSearchContent;
  if (!debouncedQuery) {
    catalogueSearchContent = (
      <EmptyState
        className="my-6"
        text="Search for an appliance specification to add."
      />
    );
  } else if (isSearching) {
    catalogueSearchContent = <EmptyState className="my-6" text="Searching…" />;
  } else if (catalogueRows.length === 0) {
    catalogueSearchContent = (
      <EmptyState className="my-6" text="No matching specifications." />
    );
  } else {
    catalogueSearchContent = catalogueRows.map(({ appliance }) => {
      const alreadyLinked = linkedApplianceIds.has(appliance.id);
      return (
        <ApplianceRow
          key={appliance.id}
          appliance={appliance}
          recommended={recommendedApplianceIds.has(appliance.id)}
          alreadyLinked={alreadyLinked}
          disabled={isPending || alreadyLinked}
          onAdd={() => onSelect(appliance.id)}
        />
      );
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 px-3.5 pt-3.5">
        <Button
          onClick={
            searching
              ? () => {
                  setSearchQuery('');
                  setSearching(false);
                }
              : onClose
          }
          variant="quaternary"
          size="sm"
        >
          {searching ? 'Back to recommended' : 'Back to items'}
        </Button>
        {productSettingsLink}
      </div>
      <div
        className={clstx(
          'flex min-h-0 flex-1 flex-col overflow-y-auto px-3.5 pb-3 transition-opacity',
          isPending && 'pointer-events-none opacity-60'
        )}
        aria-busy={isPending}
      >
        {!searching ? (
          <>
            <h4 className="flex items-center gap-1.5 pt-2 font-semibold">
              Recommended {typeLabel.plural}
              <Tooltip
                tooltipContent={
                  isSolarCategory
                    ? "Based on the property's solar potential."
                    : "Based on the property's heat-loss survey."
                }
              >
                <UntitledIcon
                  name="info-circle.3"
                  className="h-3.5 w-3.5 text-gray-400"
                />
              </Tooltip>
            </h4>

            <div className="mt-1 flex flex-col divide-y">
              {recommendations.length === 0 ? (
                <EmptyState
                  className="my-6"
                  text={`No recommended ${typeLabel.plural} to add.`}
                />
              ) : (
                recommendations.map((r) => {
                  const alreadyLinked = linkedApplianceIds.has(r.appliance.id);
                  return (
                    <ApplianceRow
                      key={r.appliance.id}
                      appliance={r.appliance}
                      recommended
                      alreadyLinked={alreadyLinked}
                      disabled={isPending || alreadyLinked}
                      onAdd={() => onSelect(r.appliance.id)}
                    />
                  );
                })
              )}
            </div>

            <Button
              variant="tertiary"
              size="sm"
              className="mt-3 w-full"
              onClick={() => setSearching(true)}
            >
              <UntitledIcon name="search-lg.3" className="h-3.5 w-3.5" />
              Find in catalogue
            </Button>
          </>
        ) : (
          <>
            <div className="mt-2">
              <RawInput
                sizeVariant="sm"
                placeholder={`Search ${typeLabel.plural}…`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="mt-1 flex flex-col divide-y">
              {catalogueSearchContent}
            </div>
          </>
        )}
      </div>
      {addEstimateAppliance.isError && (
        <div className="px-3.5 pb-3.5">
          <Alert colour="red" iconName="alert-triangle.3">
            Could not update the estimate&apos;s appliances. Please try again.
          </Alert>
        </div>
      )}
    </div>
  );
};

export default AppliancePicker;
